import { envConfig } from '#constants/envConfig';

/**
 * Хранилище картинок приложения – как у Telegram: увиденная картинка лежит на телефоне и
 * открывается сразу, в том числе без сети.
 *
 * Android перехватывает HTTPS в WebView. iOS отдаёт картинки через публичный
 * WKURLSchemeHandler (`teamplay-image:`), адрес меняется только при отрисовке.
 * Управление у обеих платформ одинаковое: пять методов нативного плагина ImageStore.
 * Без плагина (веб, старое приложение) методы отвечают «недоступно», без исключений.
 */

/**
 * Всё, что хранилище знает о себе: сколько занято, что оно делало с момента запуска и сколько
 * места осталось у телефона.
 *
 * Счётчики – **за процесс**. Журнала хранилище не ведёт, на диске у него только сами файлы, так
 * что при каждом запуске приложения всё начинается с нуля и значит "с момента старта", а не
 * "за всё время".
 */
export type ImageStoreStats = {
  /** Занято, байт. */
  bytes: number;
  files: number;
  /** Счётчики перехвата: сколько картинок отдано с диска и сколько скачано в хранилище. */
  hits: number;
  misses: number;
  /**
   * Картинки, которые видели и не оставили по своей причине: шлюз закрыт (нет сети, остывание,
   * шесть скачиваний уже идут), ключ занят, ответ не картинка. Нехватка места считается отдельно.
   */
  bypass: number;
  /** Промахи, отданные браузеру, потому что на телефоне меньше `minFreeBytes`. */
  lowSpace: number;
  /**
   * Скачивания, которые не начались: DNS, коннект, TLS, таймаут ожидания ответа. На телефоне это
   * обычная жизнь сети (лифт, метро) – только цифра в снимке. Обрыв тела и раннее закрытие потока
   * сюда не входят.
   */
  errors: number;
  /** Исключения в самом перехвате – наш баг, а не сеть. На них и жалуемся в GlitchTip. */
  faults: number;
  /** Сколько файлов удалено – по потолку или чтобы вернуть телефону запас места. */
  evictions: number;
  /** Сколько файлов удалено по сроку. */
  expired: number;
  limitBytes: number;
  /** 0 – хранить, пока не вытеснит потолок. */
  ttlDays: number;
  /** Свободно на устройстве, байт. */
  freeBytes: number;
  /** Ниже этого запаса свободного места новые картинки не сохраняются. */
  minFreeBytes: number;
  /** Есть ли куда писать. `false` – каталог не создался, не храним вообще ничего. */
  available: boolean;
  /** Когда в этом процессе закончился последний прогрев, мс; 0 – не было. */
  prewarmedAt: number;
};

export type ImageStoreSettings = {
  limitBytes: number;
  ttlDays: number;
};

/** Чем закончился проход прогрева. Хранилище отвечает, когда докачало последнюю картинку. */
export type ImageStorePrewarmResult = {
  /** Сколько скачиваний начато: наших адресов, которых в хранилище ещё не было. */
  queued: number;
  /** Сколько уже лежало – для них делать было нечего. */
  alreadyStored: number;
  /** Сколько картинок проход положил в хранилище. */
  downloaded: number;
};

const MB = 1024 * 1024;

const count = (value: unknown): number => (typeof value === 'number' && value >= 0 ? value : 0);

/** Потолки на выбор. По умолчанию 200 МБ – решение пользователя 15.09.2026. */
export const IMAGE_STORE_LIMITS = [100 * MB, 200 * MB, 500 * MB, 1024 * MB] as const;

/** Сроки на выбор, в днях; 0 – «всегда». По умолчанию 14. */
export const IMAGE_STORE_TTL_DAYS = [7, 14, 30, 0] as const;

export const DEFAULT_IMAGE_STORE_SETTINGS: ImageStoreSettings = {
  limitBytes: 200 * MB,
  ttlDays: 14,
};

interface ImageStorePlugin {
  // Partial, а не полный контракт: по мосту приходит JSON, и отвечает на него нативная часть той
  // сборки, которая стоит у человека. Чинится это нормализацией на входе, а не верой в типы.
  stats(): Promise<Partial<ImageStoreStats>>;
  getSettings(): Promise<ImageStoreSettings>;
  setSettings(options: Partial<ImageStoreSettings>): Promise<ImageStoreSettings>;
  clear(): Promise<Partial<ImageStoreStats>>;
  prewarm(options: { urls: string[] }): Promise<ImageStorePrewarmResult>;
}

// undefined - ещё не спрашивали; null - спросили, плагина нет (веб, старая сборка).
let cached: ImageStorePlugin | null | undefined;
let loading: Promise<void> | undefined;

/**
 * Загружает прокси плагина один раз и кладёт в `cached`. Возвращает `Promise<void>` и НИКОГДА сам
 * прокси: `registerPlugin` отдаёт Proxy, у которого есть любое свойство, в том числе `.then`, и
 * промис, разрешённый таким значением, зовёт несуществующий нативный метод `then` – «ImageStore.then()
 * is not implemented on android». Та же ловушка, что в `deviceSettings.ts` и `storeReview.ts`.
 */
function ensurePlugin(): Promise<void> {
  if (!envConfig.IS_NATIVE) {
    cached = null;
    return Promise.resolve();
  }
  if (cached !== undefined) {
    return Promise.resolve();
  }
  loading ??= (async () => {
    try {
      const { Capacitor, registerPlugin } = await import('@capacitor/core');
      // Нативная часть регистрирует плагин сама; нет её - нет и хранилища (веб или старая сборка).
      // Прокси присваиваем синхронно и не возвращаем (см. ловушку выше).
      cached = Capacitor.isPluginAvailable('ImageStore')
        ? registerPlugin<ImageStorePlugin>('ImageStore')
        : null;
    } catch {
      cached = null;
    }
  })();
  return loading;
}

/** Есть ли на этой платформе хранилище. После первого ответа значение известно синхронно. */
export async function isImageStoreAvailable(): Promise<boolean> {
  await ensurePlugin();
  return Boolean(cached);
}

/** Синхронный ответ, если он уже известен, иначе `undefined`. */
export const knownImageStoreAvailability = (): boolean | undefined =>
  cached === undefined ? undefined : Boolean(cached);

/**
 * Приводит ответ моста к контракту: числа – числами, отрицательных не бывает.
 *
 * `available` по умолчанию `true`: поле появилось позже остальных, и сборка, где натив о нём ещё
 * не знает, – это работающее хранилище, а не сломанное. Молчание тут значит "всё в порядке",
 * иначе старый apk сам себя объявил бы недоступным и слал бы жалобы в GlitchTip.
 */
const normalizeStats = (raw: Partial<ImageStoreStats> | null | undefined): ImageStoreStats => ({
  bytes: count(raw?.bytes),
  files: count(raw?.files),
  hits: count(raw?.hits),
  misses: count(raw?.misses),
  bypass: count(raw?.bypass),
  lowSpace: count(raw?.lowSpace),
  errors: count(raw?.errors),
  faults: count(raw?.faults),
  evictions: count(raw?.evictions),
  expired: count(raw?.expired),
  limitBytes: count(raw?.limitBytes),
  ttlDays: count(raw?.ttlDays),
  freeBytes: count(raw?.freeBytes),
  minFreeBytes: count(raw?.minFreeBytes),
  available: raw?.available !== false,
  prewarmedAt: count(raw?.prewarmedAt),
});

/** Перестало ли хранилище сохранять новое, потому что на телефоне кончается место. */
export const isImageStoreOutOfRoom = (stats: ImageStoreStats): boolean =>
  stats.minFreeBytes > 0 && stats.freeBytes < stats.minFreeBytes;

export async function getImageStoreStats(): Promise<ImageStoreStats | null> {
  await ensurePlugin();
  if (!cached) {
    return null;
  }
  try {
    return normalizeStats(await cached.stats());
  } catch {
    return null;
  }
}

export async function getImageStoreSettings(): Promise<ImageStoreSettings | null> {
  await ensurePlugin();
  if (!cached) {
    return null;
  }
  try {
    return await cached.getSettings();
  } catch {
    return null;
  }
}

/** Сохраняет и сразу применяет: лишнее по сроку и по потолку удаляется тут же, в фоне. */
export async function setImageStoreSettings(
  settings: Partial<ImageStoreSettings>
): Promise<ImageStoreSettings | null> {
  await ensurePlugin();
  if (!cached) {
    return null;
  }
  return cached.setSettings(settings);
}

/** Удаляет все картинки; отвечает тем, что осталось (обычно ноль). */
export async function clearImageStore(): Promise<ImageStoreStats | null> {
  await ensurePlugin();
  if (!cached) {
    return null;
  }
  return normalizeStats(await cached.clear());
}

const NOTHING_TO_DO: ImageStorePrewarmResult = { queued: 0, alreadyStored: 0, downloaded: 0 };

/**
 * Скачать картинки заранее. Уже лежащие пропускаются на нативной стороне, адреса не нашего
 * media-хоста – тоже.
 *
 * Ответ приходит, когда проход закончился, а не когда адреса приняты: хранилище качает их своими
 * двумя потоками, на целый каталог это десятки секунд. Вызывающему нужен именно итог – по нему
 * решается, считать ли сутки прогретыми.
 *
 * @returns итог прохода, `null` – хранилища нет или мост ответил ошибкой
 */
export async function prewarmImageStore(urls: string[]): Promise<ImageStorePrewarmResult | null> {
  await ensurePlugin();
  if (!cached) {
    return null;
  }
  if (urls.length === 0) {
    return NOTHING_TO_DO;
  }
  try {
    const result = await cached.prewarm({ urls: [...new Set(urls)] });
    return {
      queued: count(result?.queued),
      alreadyStored: count(result?.alreadyStored),
      downloaded: count(result?.downloaded),
    };
  } catch {
    return null;
  }
}
